// Carrier detection + tracking URL resolution (WSM6-2469).
//
// The platform records a tracking number against a shipment but not which carrier it belongs
// to, so the carrier has to be inferred. Signals are tried in order of trust:
//   1. Fulfillment metadata written by something that KNOWS the carrier for certain - a staff
//      pick from this dialog (`carrier`), or the shipstation app's own auto-fulfillment webhook
//      (`shipstation.carrier`).
//   2. The tracking number's own format, restricted to patterns unambiguous enough to trust:
//      UPS "1Z..." and 12/15-digit FedEx don't collide with anything else in use here; a bare
//      10-digit number is DHL Express territory.
//   3. The order's shipping method *id*. Saleor encodes which app produced an external shipping
//      method into the id itself (base64("app:<app-identifier>:<raw-id>")), and the UPS/FedEx/USPS
//      rate apps are each tied to exactly one carrier by construction - the same signal
//      wsm-storefront's checkout page already decodes for its carrier logo (see
//      ShippingMethods.tsx's vendorKeyFromAppId). More reliable than the name (below), since it
//      survives a merchant relabeling the display name - though it still reflects what was quoted
//      at checkout, not a later warehouse re-rate.
//   4. The shipping method *name* chosen at checkout - weaker still, both because a merchant can
//      freely relabel it and because it only works at all when the name happens to contain a
//      recognizable carrier keyword (Purolator/Canpar/Freightcom-brokered methods, and any
//      shipping app not in APP_ID_CARRIERS below, have no id-based signal to fall back from).
// Long bare-digit numbers (typical of both USPS domestic and DHL eCommerce) are genuinely
// ambiguous and deliberately left undetected: T-02 requires no link that leads nowhere, so an
// unresolved number is always shown as plain (but selectable) text rather than guessed.
//
// Kept identical to (and independent from) wsm-storefront's src/lib/tracking.ts - no shared
// package exists between the two apps.

export type Carrier = "ups" | "fedex" | "usps" | "dhl" | "purolator" | "canadapost" | "canpar";

export const CARRIER_OPTIONS: Array<{ value: Carrier | ""; label: string }> = [
  { value: "", label: "Unknown / Not listed" },
  { value: "ups", label: "UPS" },
  { value: "fedex", label: "FedEx" },
  { value: "usps", label: "USPS" },
  { value: "dhl", label: "DHL" },
  { value: "purolator", label: "Purolator" },
  { value: "canadapost", label: "Canada Post" },
  { value: "canpar", label: "Canpar" },
];

const CARRIER_LABELS: Record<Carrier, string> = {
  ups: "UPS",
  fedex: "FedEx",
  usps: "USPS",
  dhl: "DHL",
  purolator: "Purolator",
  canadapost: "Canada Post",
  canpar: "Canpar",
};

export function carrierLabel(carrier: Carrier | null): string | null {
  return carrier ? CARRIER_LABELS[carrier] : null;
}

const CARRIER_URL_BUILDERS: Record<Carrier, (trackingNumber: string) => string> = {
  ups: n => `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(n)}`,
  fedex: n => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`,
  usps: n => `https://tools.usps.com/tracking/?tLabels=${encodeURIComponent(n)}`,
  dhl: n =>
    `https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id=${encodeURIComponent(n)}`,
  // Verified live (WSM6-2469): the exact URL the site's own tracker page reads and acts on.
  purolator: n => `https://www.purolator.com/en/shipping/tracker?pin=${encodeURIComponent(n)}`,
  // Verified live (WSM6-2469): the real URL the site's own "Track" button navigates to.
  canadapost: n =>
    `https://www.canadapost-postescanada.ca/track-reperage/en/home#/search?searchFor=${encodeURIComponent(n)}`,
  // Verified live (WSM6-2469): the tracking form's own POST action, which also accepts GET.
  canpar: n =>
    `https://portal.canpar.com/en/track/TrackingAction.do?locale=en&type=0&reference=${encodeURIComponent(n)}`,
};

// Metadata keys a fulfillment source may have already stamped with a carrier it knows for
// certain. Checked in this order; first hit wins.
const EXPLICIT_CARRIER_METADATA_KEYS = ["carrier", "shipstation.carrier"];

export function normalizeCarrier(raw: string): Carrier | null {
  const key = raw.trim().toLowerCase();

  if (!key) return null;

  if (key.includes("ups")) return "ups";

  if (key.includes("fedex") || key === "fdx") return "fedex";

  if (key.includes("usps") || key.includes("postal") || key.includes("stamps")) return "usps";

  if (key.includes("dhl")) return "dhl";

  if (key.includes("purolator")) return "purolator";

  if (key.includes("canada post") || key.includes("canadapost")) return "canadapost";

  if (key.includes("canpar")) return "canpar";

  return null;
}

// Purolator and Canpar are deliberately absent here: Purolator's own documented format
// (10-12 digits, often prefixed "33") overlaps with FedEx's 12-digit pattern, and Canpar's
// (1 letter + 8-20 digits) is too loose/unverified to trust - same call already made for USPS's
// domestic format below. Both still resolve via the shipping-method-name keyword match instead.
const FORMAT_PATTERNS: Array<[Carrier, RegExp]> = [
  ["ups", /^1Z[0-9A-Z]{16}$/],
  ["fedex", /^\d{12}$|^\d{15}$/],
  ["usps", /^[A-Z]{2}\d{9}US$/],
  ["dhl", /^\d{10}$/],
  // 16-digit domestic, or UPU S10 international ending "CA" - the same international shape as
  // USPS's above but with Canada Post's own country suffix, so the two never collide.
  ["canadapost", /^\d{16}$|^[A-Z]{2}\d{9}CA$/],
];

function detectByFormat(trackingNumber: string): Carrier | null {
  const value = trackingNumber.trim().toUpperCase();

  for (const [carrier, pattern] of FORMAT_PATTERNS) {
    if (pattern.test(value)) return carrier;
  }

  return null;
}

// Shipping-app manifest identifiers (wsm-app-platform/external_apps/*/manifest.py), mapped to
// the carrier each app is exclusively tied to. Kept identical to wsm-storefront's
// ShippingMethods.tsx APP_ID_VENDOR_KEYS - both decode the same Saleor-encoded id.
const APP_ID_CARRIERS: Record<string, Carrier> = {
  "ups.shipping": "ups",
  "fedex.shipping": "fedex",
  "usps.shipping": "usps",
};

/**
 * Carrier encoded into an app-provided shipping method's own id
 * (base64("app:<app-identifier>:<raw-id>")). Returns null for a native/manual rate (not base64,
 * or a recognized shape but an app identifier not in APP_ID_CARRIERS - e.g. Freightcom, which
 * brokers multiple carriers and so isn't tied to just one).
 */
export function carrierFromShippingMethodId(id: string | null | undefined): Carrier | null {
  if (!id) return null;

  try {
    const decoded = atob(id); // "app:ups.shipping:ups:02"
    const appIdentifier = decoded.split(":")[1] ?? "";

    return APP_ID_CARRIERS[appIdentifier] ?? null;
  } catch {
    return null;
  }
}

export type TrackingContext = {
  metadata?: Array<{ key: string; value: string }> | null;
  shippingMethodName?: string | null;
  /** The order/checkout's shippingMethod.id, if available - see carrierFromShippingMethodId. */
  shippingMethodId?: string | null;
};

export type ResolvedTracking = {
  /** The value to display (the tracking number itself, or the full URL for T-06). */
  label: string;
  /** Link target, or null when no carrier could be resolved with confidence. */
  url: string | null;
  /** The resolved carrier, or null when unresolved (or the value was a bare URL). */
  carrier: Carrier | null;
};

/** The explicit carrier already recorded on this fulfillment's metadata, if any. */
export function explicitCarrierFromMetadata(
  metadata: Array<{ key: string; value: string }> | null | undefined,
): Carrier | null {
  for (const key of EXPLICIT_CARRIER_METADATA_KEYS) {
    const entry = metadata?.find(m => m.key === key);

    if (entry?.value) {
      const carrier = normalizeCarrier(entry.value);

      if (carrier) return carrier;
    }
  }

  return null;
}

/**
 * Carrier to prefill the "Add Tracking Code" dialog's picker with. Editing a fulfillment that
 * already has a tracking number keeps today's behavior (the carrier resolved from it, same
 * trust order as resolveTracking). Adding tracking for the first time has no number yet to
 * resolve from, so this guesses from the order's shipping method id (preferred - see
 * carrierFromShippingMethodId) or name instead of defaulting to blank - staff can still override
 * it, since a merchant can rename that method or a warehouse can re-rate to a different carrier
 * than what was paid for.
 */
export function defaultCarrierForFulfillment(
  fulfillment:
    | { trackingNumber?: string | null; metadata?: Array<{ key: string; value: string }> | null }
    | null
    | undefined,
  shippingMethodName: string | null | undefined,
  shippingMethodId?: string | null,
): Carrier | "" {
  if (fulfillment?.trackingNumber) {
    return (
      resolveTracking(fulfillment.trackingNumber, {
        metadata: fulfillment.metadata,
        shippingMethodName,
        shippingMethodId,
      }).carrier || ""
    );
  }

  return (
    carrierFromShippingMethodId(shippingMethodId) ||
    normalizeCarrier(shippingMethodName ?? "") ||
    ""
  );
}

/** Resolve a stored tracking value to a display label + optional link. Never throws. */
export function resolveTracking(
  trackingNumber: string,
  context: TrackingContext = {},
): ResolvedTracking {
  const value = trackingNumber.trim();

  if (!value) return { label: "", url: null, carrier: null };

  // T-06: the recorded value is already a full address - link to it as-is, carrier-agnostic.
  if (/^https?:\/\//i.test(value)) {
    return { label: value, url: value, carrier: null };
  }

  const carrier =
    explicitCarrierFromMetadata(context.metadata) ||
    detectByFormat(value) ||
    carrierFromShippingMethodId(context.shippingMethodId) ||
    normalizeCarrier(context.shippingMethodName ?? "");

  return {
    label: value,
    url: carrier ? CARRIER_URL_BUILDERS[carrier](value) : null,
    carrier,
  };
}
